import fetchInGameCouponCount from 'features/customerPanel/cpCoupons/store/cpCoupons.action';
import { handleBalanceData } from 'features/customerPanel/store/cpPanel.action';
import { fetchBanners } from 'features/Home';
import { getNotificationPermission } from 'features/NativeNotification/NativeNotificationManager';
import { Freebet } from 'features/offers/interface/offers.interface';
import { fetchBonusesAndFreebets } from 'features/offers/store/offers.action';
import WebView from 'features/webView/utils';
import { ThunkDispatch } from 'redux-thunk';
import { MAIN_BALANCE, vipLevelArray } from 'shared/constants/common';
import { BetslipEvents, setDataLayer } from 'shared/constants/datalayer';
import { Action, CouponState } from 'shared/interface';
import * as actionTypes from 'shared/store/action-types';
import { calculateTax, updateUserDetails } from 'shared/store/coupon.action';
import { triggerSurveyForUnverifiedUser } from 'shared/util/triggerSurveyForUnverifiedUser';
import { createAction, createSessionCookie, encryptData, getBetsDetail, getRemainingFreebetBalance, setUserDetails } from 'shared/util/utility';
import { CommonAPI, CustomerApi, LoginApi, TransactionApi } from '../../../api';
import { AccountInformationResponseDto } from '../../../api/common.generated';
import { reactNativeEvent } from '../../../shared/react-native/event';
import { getAnalyticalData } from '../../../shared/util/analytical';
import { vipUserDataLayer } from '../util/utility';

const setLastDepositMethod = (
	accountResponse: AccountInformationResponseDto,
	dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>,
) => {
	if (accountResponse.playersProfile?.lastDepositMethod) {
		const depositMethod = {
			lastDepositMethod: accountResponse.playersProfile?.lastDepositMethod,
			lastDepositBankId: accountResponse.playersProfile?.lastDepositBankId ? accountResponse.playersProfile?.lastDepositBankId : null,
		};
		dispatch(createAction(actionTypes.LAST_DEPOSIT_METHOD, depositMethod));
	}
};

const loginUser = (loginActionData: any, coupon: CouponState) => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		dispatch(createAction(actionTypes.LOGIN_INIT, loginActionData));
		try {
			const response = await LoginApi.loginUser(loginActionData);
			if (response && response.data && !response.data.isError) {
				const { loginData, lastStake, device } = response.data.data;
				if (response.headers['x-odds-session']) {
					createSessionCookie(response.headers['x-odds-session']);
				}
				const accountResponse = await CommonAPI.Customer.getAccountInformation();
				if (accountResponse && accountResponse.data && !accountResponse.data.isError) {
					loginData.userId = accountResponse.data.data.verificationStatusData?.customerData?.customerId;
					dispatch(
						createAction(actionTypes.LOGIN_SUCCESS, {
							...loginData,
							lastStake: lastStake,
						}),
					);
					setLastDepositMethod(accountResponse.data.data, dispatch);

					WebView.broadcastAnalytics('LOGIN', { userId: accountResponse.data.data.verificationStatusData?.customerData?.customerId });

					reactNativeEvent().send('LOGIN', { id: accountResponse.data.data.verificationStatusData?.customerData?.customerId });
					const { blocks, outcomes } = getBetsDetail();
					if (blocks.flat().length + outcomes.length !== 0) {
						setDataLayer(BetslipEvents.BET_RESUME(getAnalyticalData(coupon)));
					}

					await setUserDetails({
						...loginData,
						lastStake: lastStake,
						verificationStatus: accountResponse.data.data.verificationStatusData.account.verificationStatus,
						registeredOn: accountResponse.data.data.verificationStatusData.account.registeredOn,
					});
				}

				const balanceData = {
					selectedBalanceType: MAIN_BALANCE,
					mainBalance: loginData.balance,
					bonusBalance: loginData.calculatedBonus,
					bonusBalanceList: loginData.bonusBalanceList,
					depositCounter: loginData.depositCounter,
				};
				await dispatch(handleBalanceData(balanceData)); // await is for high traffic issue with login

				const bonusesAndFreebets = await dispatch(fetchBonusesAndFreebets()); // await is for high traffic issue with login

				const bonusesQueued = bonusesAndFreebets?.data?.bonusList?.queue || [];
				const freebetsActive = bonusesAndFreebets?.data?.freebetList?.active || [];
				const freebets = freebetsActive.filter((offer): offer is Freebet => 'offerFreebetId' in offer);
				const freebetsBalance = getRemainingFreebetBalance(freebets);
				const summarizedBonusesQueued = bonusesQueued.reduce((acc, bonus) => acc + (bonus.bonusValue || 0), 0);
				if (accountResponse && accountResponse.data && !accountResponse.data.isError) {
					setDataLayer({
						event: 'login-success',
						notificationsEnabled: getNotificationPermission(),
						customerID: accountResponse?.data?.data?.verificationStatusData?.customerData?.customerId,
						email: loginData.email,
						login: loginData.email,
						firstName: loginData.firstName,
						lastName: loginData.lastName,
						language: loginData.language,
						mobile: device === 'desktop' ? false : true,
						sessionStart: Math.floor(Date.now() / 1000),
						status: loginData.status,
						vip: vipUserDataLayer(vipLevelArray[loginData.vipStatusData]) || loginData.vip,
						currencyCode: loginData.currencyCode,
						accountBalance: loginData.balance,
						bonusBalance: loginData.calculatedBonus + summarizedBonusesQueued,
						freebetBalance: freebetsBalance,
					});

					triggerSurveyForUnverifiedUser(
						accountResponse.data.data.verificationStatusData.account.registeredOn,
						accountResponse.data.data.verificationStatusData.account.verificationStatus,
					);
				}

				await dispatch(getVerificationStatus()); // await is for high traffic issue with login

				await dispatch(getSlipsSettledData()); // await is for high traffic issue with login

				await dispatch(fetchInGameCouponCount()); // await is for high traffic issue with login

				await dispatch(updateUserDetails());
				await dispatch(calculateTax());
				await dispatch(fetchBanners(false));

				// const customerInfo = await CustomerApi.getCustomerAndAccountData();
				// if (customerInfo && customerInfo.data && !customerInfo.data.isError) {
				// 	localStorage.setItem(
				// 		'customerAccountInfo',
				// 		JSON.stringify({ ...customerInfo.data.data })
				// 	);
				// }
				// dispatch(getUnreadMessage());

				if (loginActionData.keepSignedIn) {
					localStorage.setItem('keepSignedIn', encryptData({ ...loginActionData }).toString());
				} else {
					localStorage.setItem('keepSignedIn', '{}');
				}
			}
			return response.data;
		} catch (err: any) {
			console.error(err);
			dispatch(createAction(actionTypes.LOGIN_FAIL, { error: err.message || '' }));
			return null;
		}
	};
};

const getAvatarProfile = () => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		try {
			const resp = await CommonAPI.Customer.getAccountInformation();
			if (resp.data.isError) {
				console.error('error: ', resp.data.message);
			} else {
				const avatar = resp.data.data.playersProfile.avatarFile;
				dispatch(createAction(actionTypes.SET_AVATAR, avatar));
				const playerBiggestOdd = resp.data.data.playersProfile.biggestOdd;
				const playerFavouriteCategory = resp.data.data.playersProfile.favouriteCategoryName;
				const playerBiggestWin = resp.data.data.playersProfile.biggestWin;
				const playerTotalBets = resp.data.data.playersProfile.totalBets;
				const emptyStats = resp.data.data.playersProfile.emptyStatistics;
				const favouriteSportIcon = resp.data.data.playersProfile.favouriteSportIcon;
				const playerStats = {
					biggestOdd: playerBiggestOdd,
					favouriteCategory: playerFavouriteCategory,
					biggestWin: playerBiggestWin,
					totalBets: playerTotalBets,
					favouriteSportIcon: favouriteSportIcon,
					emptyStats: emptyStats,
				};
				dispatch(createAction(actionTypes.PLAYER_STATS, playerStats));
				setLastDepositMethod(resp.data.data, dispatch);
				dispatch(createAction(actionTypes.SET_COLLAPSED_MARKET, resp?.data?.data?.playersProfile?.collapsedMarkets));
			}
		} catch (error) {
			console.error('error: ', error);
		}
	};
};

const getCustomStake = () => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		try {
			// TODO Change to a new endpoint with just the defaultStakes https://betfan.atlassian.net/browse/BBS-451
			const resp = await CommonAPI.Customer.getAccountInformation();
			if (resp.data.isError) {
				console.error('error: ', resp.data.message);
			} else {
				const newDefualtStake = resp.data.data.playersProfile.defaultStakes;
				dispatch(createAction(actionTypes.UPDATE_STAKE_VALUES, newDefualtStake));
				setLastDepositMethod(resp.data.data, dispatch);
			}
		} catch (error) {
			console.error('error: ', error);
		}
	};
};
const getVipData = () => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		try {
			const resp = await CustomerApi.getVipData();
			const vipStatusData = resp.data.data?.vipStatusData;
			dispatch(createAction(actionTypes.VIP, vipStatusData));
		} catch (error) {
			console.error('error: ', error);
		}
	};
};

const getVerificationStatus = () => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		dispatch(createAction(actionTypes.USER_VERIFICATION_STATUS_INIT));
		try {
			const response = await LoginApi.verificationStatus();
			const verificationData = response.data.data.verificationStatusData;
			dispatch(createAction(actionTypes.USER_VERIFICATION_STATUS_SUCCESS, verificationData));
		} catch (error) {
			dispatch(createAction(actionTypes.USER_VERIFICATION_STATUS_FAIL));
			return null;
		}
	};
};

const getSlipsSettledData = () => {
	return async (dispatch: ThunkDispatch<Record<string, unknown>, Record<string, unknown>, Action>) => {
		try {
			const response = await TransactionApi.slipsSettled();
			const slipWinData = response.data.data;
			const winAmount = slipWinData?.settledSlipData.reduce((sum: number, curr: any) => (sum = sum + curr.confirmedReturn), 0);
			dispatch(createAction(actionTypes.WIN_DATA, winAmount));
		} catch (error) {
			console.error(error);
		}
	};
};

const showLoginModal = () => {
	return {
		type: actionTypes.SHOW_MODAL,
	};
};

const hideLoginModal = () => {
	return {
		type: actionTypes.HIDE_MODAL,
	};
};

const showRemindModal = () => {
	return {
		type: actionTypes.SHOW_REMIND_MODAL,
	};
};

const hideRemindModal = () => {
	return {
		type: actionTypes.HIDE_REMIND_MODAL,
	};
};

const showVerificationModal = () => {
	return {
		type: actionTypes.SHOW_VERIFICATION_MODAL,
	};
};
const hideVerificationModal = () => {
	return {
		type: actionTypes.HIDE_VERIFICATION_MODAL,
	};
};

export {
	loginUser,
	getVerificationStatus,
	getVipData,
	getAvatarProfile,
	showLoginModal,
	hideLoginModal,
	showRemindModal,
	hideRemindModal,
	showVerificationModal,
	hideVerificationModal,
	getCustomStake,
};
